/**
 * API Client for Comments System
 * Task 5.9: SDK API Client Implementation
 */

import type { ElementFingerprint, Comment, TeamMember, CommentType } from './types';

/**
 * Converts a snake_case string to camelCase
 */
function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

/**
 * Converts a camelCase string to snake_case
 */
function camelToSnake(str: string): string {
  return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
}

/**
 * Recursively converts all keys in an object from snake_case to camelCase.
 * Handles nested objects and arrays.
 */
function convertKeysToCamelCase<T>(obj: unknown): T {
  if (obj === null || obj === undefined) {
    return obj as T;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => convertKeysToCamelCase(item)) as T;
  }

  if (typeof obj === 'object') {
    const converted: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      const camelKey = snakeToCamel(key);
      converted[camelKey] = convertKeysToCamelCase(value);
    }
    return converted as T;
  }

  return obj as T;
}

/**
 * Recursively converts all keys in an object from camelCase to snake_case.
 * Handles nested objects and arrays. Used for outgoing API requests.
 */
function convertKeysToSnakeCase<T>(obj: unknown): T {
  if (obj === null || obj === undefined) {
    return obj as T;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => convertKeysToSnakeCase(item)) as T;
  }

  if (typeof obj === 'object') {
    const converted: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      const snakeKey = camelToSnake(key);
      converted[snakeKey] = convertKeysToSnakeCase(value);
    }
    return converted as T;
  }

  return obj as T;
}

/**
 * Standard API response wrapper from the Go backend
 */
interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
}

/**
 * User registration request - sent to POST /api/v1/users/me
 */
export interface RegisterUserRequest {
  authProviderId: string;  // Auth0 'sub' claim
  email: string;
  name: string;
  givenName: string;
  familyName: string;
  picture: string;
}

/**
 * User profile response from the API
 */
export interface RegisteredUser {
  id: string;              // Internal UUID
  authProviderId: string;  // Auth0 'sub' claim
  email: string;
  name: string;
  givenName: string;
  familyName: string;
  picture: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Configuration for the ApiClient
 */
export interface ApiClientConfig {
  baseUrl: string;
  siteId: string;
  environmentId: string;
  authToken?: string;  // id_token for hackweek, MAS token for production (TODO: upgrade)
  timeout?: number;
}

/**
 * Data required to create a new comment (uses camelCase - converted to snake_case when sent to API)
 */
export interface CreateCommentData {
  pagePath: string;
  elementFingerprint: ElementFingerprint;
  content: string;
  type: CommentType;
  threadId?: string;
}

/**
 * Data for updating an existing comment
 */
export interface UpdateCommentData {
  content?: string;
}

// ============================================================================
// Jira Integration Types
// ============================================================================

/**
 * Jira connection status from the API
 */
export interface JiraConnectionResponse {
  id: string;
  siteId: string;
  jiraCloudId: string;
  jiraSiteUrl: string;
  defaultProjectKey: string | null;
  defaultIssueType: string | null;
  connectedBy: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * AI-generated ticket content from the API
 */
export interface GeneratedJiraTicket {
  summary: string;
  description: string;
}

/**
 * Data to create a Jira ticket
 */
export interface CreateJiraTicketData {
  commentId: string;
  summary: string;
  description: string;
  projectKey?: string;
  issueType?: string;
}

/**
 * Created ticket response from the API
 */
export interface CreatedJiraTicket {
  ticketKey: string;
  ticketUrl: string;
}

/**
 * Linked ticket for a comment
 */
export interface CommentJiraTicket {
  id: string;
  commentId: string;
  jiraIssueKey: string;
  jiraIssueUrl: string;
  jiraProjectKey: string;
  createdBy: string;
  createdAt: string;
}

/**
 * Jira project from the API
 */
export interface JiraProject {
  id: string;
  key: string;
  name: string;
  avatarUrl?: string;
}

/**
 * Jira issue type from the API
 */
export interface JiraIssueType {
  id: string;
  name: string;
  description?: string;
  iconUrl?: string;
}

/**
 * API Client for the All Hands Comments API
 *
 * Implements the contract expected by MultidevCollaborationProvider:
 * - getComments() - fetch all comments for site/branch
 * - createComment(data) - create new comment
 * - updateComment(id, data) - update existing comment
 * - deleteComment(id) - delete comment
 * - resolveComment(id) - mark comment resolved
 * - reopenComment(id) - reopen resolved comment
 * - reAnchorComment(id, fingerprint) - update element fingerprint
 * - pollComments(since) - fetch comments updated since timestamp
 * - getTeamMembers() - fetch team members for @mention autocomplete
 */
export class ApiClient {
  private baseUrl: string;
  private siteId: string;
  private environmentId: string;
  private authToken?: string;  // id_token for hackweek, MAS token for production (TODO: upgrade)
  private timeout: number;

  constructor(config: ApiClientConfig) {
    if (!config.baseUrl) {
      throw new Error('baseUrl is required');
    }
    if (!config.siteId) {
      throw new Error('siteId is required');
    }
    if (!config.environmentId) {
      throw new Error('environmentId is required');
    }

    // Remove trailing slash from baseUrl
    this.baseUrl = config.baseUrl.replace(/\/$/, '');
    this.siteId = config.siteId;
    this.environmentId = config.environmentId;
    this.authToken = config.authToken;
    this.timeout = config.timeout || 30000; // 30 seconds default
  }

  /**
   * Update the auth token (id_token for hackweek, MAS token for production)
   * Called after user authentication completes
   * TODO: Upgrade to MAS token exchange for production
   */
  setAuthToken(token: string | null): void {
    this.authToken = token || undefined;
  }

  /**
   * Get the base URL for comments API endpoints
   * Note: environmentId is URL-encoded to handle branches with slashes (e.g., feature/test)
   */
  private get commentsBaseUrl(): string {
    return `${this.baseUrl}/api/v1/sites/${this.siteId}/environments/${encodeURIComponent(this.environmentId)}/comments`;
  }

  /**
   * Fetches all comments for the site/branch
   */
  async getComments(): Promise<Comment[]> {
    const response = await this.request<ApiResponse<Comment[]>>(this.commentsBaseUrl, {
      method: 'GET',
    });
    return response.data || [];
  }

  /**
   * Creates a new comment
   */
  async createComment(data: CreateCommentData): Promise<Comment> {
    const response = await this.request<ApiResponse<Comment>>(this.commentsBaseUrl, {
      method: 'POST',
      body: JSON.stringify(convertKeysToSnakeCase(data)),
    });
    return response.data;
  }

  /**
   * Updates an existing comment
   */
  async updateComment(commentId: string, data: UpdateCommentData): Promise<Comment> {
    const url = `${this.commentsBaseUrl}/${commentId}`;
    const response = await this.request<ApiResponse<Comment>>(url, {
      method: 'PATCH',
      body: JSON.stringify(convertKeysToSnakeCase(data)),
    });
    return response.data;
  }

  /**
   * Deletes a comment
   */
  async deleteComment(commentId: string): Promise<void> {
    const url = `${this.commentsBaseUrl}/${commentId}`;
    await this.request<ApiResponse<null>>(url, {
      method: 'DELETE',
    });
  }

  /**
   * Resolves a comment (marks as resolved)
   */
  async resolveComment(commentId: string): Promise<Comment> {
    const url = `${this.commentsBaseUrl}/${commentId}/resolve`;
    const response = await this.request<ApiResponse<Comment>>(url, {
      method: 'POST',
    });
    return response.data;
  }

  /**
   * Reopens a resolved comment
   */
  async reopenComment(commentId: string): Promise<Comment> {
    const url = `${this.commentsBaseUrl}/${commentId}/reopen`;
    const response = await this.request<ApiResponse<Comment>>(url, {
      method: 'POST',
    });
    return response.data;
  }

  /**
   * Re-anchors a comment to a new element
   */
  async reAnchorComment(commentId: string, fingerprint: ElementFingerprint): Promise<Comment> {
    const url = `${this.commentsBaseUrl}/${commentId}/reanchor`;
    const response = await this.request<ApiResponse<Comment>>(url, {
      method: 'POST',
      body: JSON.stringify(convertKeysToSnakeCase({ elementFingerprint: fingerprint })),
    });
    return response.data;
  }

  /**
   * Polls for comments updated since the given timestamp
   */
  async pollComments(since: string): Promise<Comment[]> {
    const url = `${this.commentsBaseUrl}/poll?since=${encodeURIComponent(since)}`;
    const response = await this.request<ApiResponse<Comment[]>>(url, {
      method: 'GET',
    });
    return response.data || [];
  }

  /**
   * Marks a comment as read by the current user
   * Returns the updated comment with isUnread: false
   * Requirements: docs/specs/011526_track-comment-reads/requirements.md FR-3
   */
  async markAsRead(commentId: string): Promise<Comment> {
    const url = `${this.commentsBaseUrl}/${commentId}/read`;
    const response = await this.request<ApiResponse<Comment>>(url, {
      method: 'POST',
    });
    return response.data;
  }

  /**
   * Marks a comment as unread by the current user
   * Returns the updated comment with isUnread: true
   * Requirements: docs/specs/011526_track-comment-reads/requirements.md FR-10
   */
  async markAsUnread(commentId: string): Promise<Comment> {
    const url = `${this.commentsBaseUrl}/${commentId}/unread`;
    const response = await this.request<ApiResponse<Comment>>(url, {
      method: 'POST',
    });
    return response.data;
  }

  /**
   * Fetches team members for @mention autocomplete
   */
  async getTeamMembers(): Promise<TeamMember[]> {
    const url = `${this.baseUrl}/api/v1/sites/${this.siteId}/team-members`;
    const response = await this.request<ApiResponse<TeamMember[]>>(url, {
      method: 'GET',
    });
    return response.data || [];
  }

  // ============================================================================
  // Jira Integration Methods
  // ============================================================================

  /**
   * Gets the Jira connection status for this site
   */
  async getJiraConnection(): Promise<JiraConnectionResponse | null> {
    const url = `${this.baseUrl}/api/v1/sites/${this.siteId}/jira/connection`;
    try {
      const response = await this.request<ApiResponse<JiraConnectionResponse>>(url, {
        method: 'GET',
      });
      return response.data;
    } catch (error) {
      // 404 means no connection exists
      if (error instanceof Error && error.message.includes('Not Found')) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Generates AI ticket content for a comment
   */
  async generateJiraTicket(commentId: string): Promise<GeneratedJiraTicket> {
    const url = `${this.baseUrl}/api/v1/sites/${this.siteId}/jira/generate-ticket`;
    const response = await this.request<ApiResponse<GeneratedJiraTicket>>(url, {
      method: 'POST',
      body: JSON.stringify({ comment_id: commentId }),
    });
    return response.data;
  }

  /**
   * Creates a Jira ticket from a comment
   */
  async createJiraTicket(data: CreateJiraTicketData): Promise<CreatedJiraTicket> {
    const url = `${this.baseUrl}/api/v1/sites/${this.siteId}/jira/create-ticket`;
    const response = await this.request<ApiResponse<CreatedJiraTicket>>(url, {
      method: 'POST',
      body: JSON.stringify(convertKeysToSnakeCase(data)),
    });
    return response.data;
  }

  /**
   * Gets the linked Jira ticket for a comment
   */
  async getCommentJiraTicket(commentId: string): Promise<CommentJiraTicket | null> {
    const url = `${this.baseUrl}/api/v1/sites/${this.siteId}/comments/${commentId}/jira-ticket`;
    try {
      const response = await this.request<ApiResponse<CommentJiraTicket>>(url, {
        method: 'GET',
      });
      return response.data;
    } catch (error) {
      // 404 means no ticket exists
      if (error instanceof Error && error.message.includes('Not Found')) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Gets available Jira projects for the connected site
   */
  async getJiraProjects(): Promise<JiraProject[]> {
    const url = `${this.baseUrl}/api/v1/sites/${this.siteId}/jira/projects`;
    const response = await this.request<ApiResponse<JiraProject[]>>(url, {
      method: 'GET',
    });
    return response.data || [];
  }

  /**
   * Gets available issue types for a Jira project
   */
  async getJiraIssueTypes(projectKey: string): Promise<JiraIssueType[]> {
    const url = `${this.baseUrl}/api/v1/sites/${this.siteId}/jira/projects/${projectKey}/issue-types`;
    const response = await this.request<ApiResponse<JiraIssueType[]>>(url, {
      method: 'GET',
    });
    return response.data || [];
  }

  /**
   * Gets the Jira OAuth authorization URL.
   * When navigating to this URL, the user will be redirected to Atlassian
   * to authorize the Jira connection, then back to the callback URL.
   * @param returnUrl - Optional URL to redirect back to after OAuth completes
   * @returns The full OAuth authorization URL
   */
  getJiraOAuthUrl(returnUrl?: string): string {
    let url = `${this.baseUrl}/api/v1/sites/${this.siteId}/jira/oauth/authorize`;
    if (returnUrl) {
      url += `?return_url=${encodeURIComponent(returnUrl)}`;
    }
    return url;
  }

  // ============================================================================
  // Auth Integration Methods
  // ============================================================================

  /**
   * Register or update a user in the system
   * Called after login to sync Auth0 user profile with internal user table
   * @param profile - User profile from decoded id_token
   * @returns Registered user with internal UUID
   */
  async registerUser(profile: RegisterUserRequest): Promise<RegisteredUser> {
    const url = `${this.baseUrl}/api/v1/users/me`;
    const response = await this.request<ApiResponse<RegisteredUser>>(url, {
      method: 'POST',
      body: JSON.stringify(convertKeysToSnakeCase(profile)),
    });
    return response.data;
  }

  /**
   * Exchange Auth0 access token for site-scoped MAS token
   * @param accessToken - Auth0 access token from X-Pantheon-Access-Token cookie
   * @param siteId - Site UUID to scope the MAS token to
   * @returns MAS token and expiration time in seconds
   */
  async exchangeForMASToken(accessToken: string, siteId: string): Promise<{ token: string; expiresIn: number }> {
    const url = `${this.baseUrl}/api/v1/auth/exchange`;
    const response = await this.request<ApiResponse<{ token: string; expires_in: number }>>(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ site_id: siteId }),
    });

    return {
      token: response.data.token,
      expiresIn: response.data.expires_in,
    };
  }

  /**
   * Internal request handler with error handling
   */
  private async request<T>(url: string, options: RequestInit): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Add authorization header if token is provided
    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }

    const requestOptions: RequestInit = {
      ...options,
      headers: {
        ...headers,
        ...(options.headers as Record<string, string> || {}),
      },
    };

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(url, {
        ...requestOptions,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        await this.handleErrorResponse(response);
      }

      // Handle 204 No Content
      if (response.status === 204) {
        return undefined as T;
      }

      const data = await response.json();
      // Convert snake_case keys from Go API to camelCase for TypeScript
      return convertKeysToCamelCase<T>(data);
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error('Request timeout');
        }
        throw error;
      }
      throw new Error('Unknown error occurred');
    }
  }

  /**
   * Handles error responses from the API
   */
  private async handleErrorResponse(response: Response): Promise<never> {
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

    try {
      const errorData = await response.json();
      if (errorData.error) {
        errorMessage = errorData.error;
      }
      if (errorData.details) {
        errorMessage += ` - ${JSON.stringify(errorData.details)}`;
      }
    } catch {
      // If JSON parsing fails, use the default error message
    }

    // Throw specific error types based on status code
    switch (response.status) {
      case 400:
        throw new Error(`Bad Request: ${errorMessage}`);
      case 401:
        throw new Error(`Unauthorized: ${errorMessage}`);
      case 403:
        throw new Error(`Forbidden: ${errorMessage}`);
      case 404:
        throw new Error(`Not Found: ${errorMessage}`);
      case 500:
      case 502:
      case 503:
        throw new Error(`Server Error: ${errorMessage}`);
      default:
        throw new Error(errorMessage);
    }
  }
}
